/**
 * HC Dashboard API Client
 * Axios-based API client for Health Check endpoints
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import type {
  ApiResponse,
  SystemHealth,
  DashboardMetrics,
  Observation,
  ObservationListResponse,
  ObservationLabel,
  Policy,
  PolicyStats,
  Audit,
  AuditListResponse,
  Agent,
} from '../types';

// API Base URL from environment or default
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1/hc';

// Create axios instance with defaults
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth tokens and required headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Required contract headers
    config.headers['X-Request-Id'] = `req_${crypto.randomUUID()}`;
    config.headers['X-Client-Version'] = '2.0.0';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.warn('Unauthorized request - token may be expired');
      // Clear stale token and reload to show login page
      localStorage.removeItem('auth_token');
      localStorage.removeItem('hc_user');
      if (window.location.pathname !== '/hc' && window.location.pathname !== '/hc/') {
        window.location.href = '/hc';
      }
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Health Endpoints
// =============================================================================

export const healthApi = {
  /**
   * Get HC subsystem health status
   */
  getHealth: async (): Promise<ApiResponse<SystemHealth>> => {
    const response = await apiClient.get<ApiResponse<SystemHealth>>('/health');
    return response.data;
  },

  /**
   * Get HC system info and metadata
   */
  getInfo: async (): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>('/info');
    return response.data;
  },
};

// =============================================================================
// Dashboard Endpoints
// =============================================================================

export const dashboardApi = {
  /**
   * Get aggregated dashboard metrics
   */
  getMetrics: async (): Promise<ApiResponse<DashboardMetrics>> => {
    const response = await apiClient.get<ApiResponse<DashboardMetrics>>('/dashboard/metrics');
    return response.data;
  },

  /**
   * Get dashboard summary for quick overview
   */
  getSummary: async (): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>('/dashboard/summary');
    return response.data;
  },
};

// =============================================================================
// Observation Endpoints
// =============================================================================

export interface ObservationFilters {
  page?: number;
  limit?: number;
  severity?: string;
  category?: string;
  source?: string;
  startDate?: string;
  endDate?: string;
  labeled?: boolean;
}

export const observationApi = {
  /**
   * List observations with filtering and pagination
   */
  list: async (filters: ObservationFilters = {}): Promise<ObservationListResponse> => {
    const response = await apiClient.get<ObservationListResponse>('/observations', {
      params: filters,
    });
    return response.data;
  },

  /**
   * Get single observation by ID
   */
  get: async (id: string): Promise<ApiResponse<Observation>> => {
    const response = await apiClient.get<ApiResponse<Observation>>(`/observations/${id}`);
    return response.data;
  },

  /**
   * List unlabeled observations for review
   */
  getUnlabeled: async (limit: number = 50): Promise<ObservationListResponse> => {
    const response = await apiClient.get<ObservationListResponse>('/observations/unlabeled', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Label an observation (true_positive, false_positive, deferred)
   */
  label: async (
    id: string,
    labelData: Omit<ObservationLabel, 'labeled_at'>
  ): Promise<ApiResponse<Observation>> => {
    const response = await apiClient.post<ApiResponse<Observation>>(
      `/observations/${id}/label`,
      labelData
    );
    return response.data;
  },
};

// =============================================================================
// Policy Endpoints
// =============================================================================

export const policyApi = {
  /**
   * List all policies
   */
  list: async (): Promise<ApiResponse<Policy[]>> => {
    const response = await apiClient.get<ApiResponse<Policy[]>>('/policies');
    return response.data;
  },

  /**
   * Get single policy by ID
   */
  get: async (id: string): Promise<ApiResponse<Policy>> => {
    const response = await apiClient.get<ApiResponse<Policy>>(`/policies/${id}`);
    return response.data;
  },

  /**
   * Create new policy
   */
  create: async (policy: Omit<Policy, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Policy>> => {
    const response = await apiClient.post<ApiResponse<Policy>>('/policies', policy);
    return response.data;
  },

  /**
   * Update existing policy
   */
  update: async (id: string, policy: Partial<Policy>): Promise<ApiResponse<Policy>> => {
    const response = await apiClient.put<ApiResponse<Policy>>(`/policies/${id}`, policy);
    return response.data;
  },

  /**
   * Delete policy
   */
  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete<ApiResponse<void>>(`/policies/${id}`);
    return response.data;
  },

  /**
   * Get policy statistics
   */
  getStats: async (id: string): Promise<ApiResponse<PolicyStats>> => {
    const response = await apiClient.get<ApiResponse<PolicyStats>>(`/policies/${id}/stats`);
    return response.data;
  },

  /**
   * Get summary stats for all policies
   */
  getStatsSummary: async (): Promise<ApiResponse<Record<string, PolicyStats>>> => {
    const response = await apiClient.get<ApiResponse<Record<string, PolicyStats>>>('/policies/stats/summary');
    return response.data;
  },
};

// =============================================================================
// Audit Endpoints
// =============================================================================

export interface AuditFilters {
  page?: number;
  limit?: number;
  status?: string;
  action?: string;
  policyId?: string;
  startDate?: string;
  endDate?: string;
}

export const auditApi = {
  /**
   * List audits with filtering and pagination
   */
  list: async (filters: AuditFilters = {}): Promise<AuditListResponse> => {
    const response = await apiClient.get<AuditListResponse>('/audits', {
      params: filters,
    });
    return response.data;
  },

  /**
   * Get single audit by ID
   */
  get: async (id: string): Promise<ApiResponse<Audit>> => {
    const response = await apiClient.get<ApiResponse<Audit>>(`/audits/${id}`);
    return response.data;
  },

  /**
   * Get recent audits for dashboard
   */
  getRecent: async (limit: number = 10): Promise<ApiResponse<Audit[]>> => {
    const response = await apiClient.get<ApiResponse<Audit[]>>('/audits/recent', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Rollback an audit action
   */
  rollback: async (id: string): Promise<ApiResponse<Audit>> => {
    const response = await apiClient.post<ApiResponse<Audit>>(`/audits/${id}/rollback`);
    return response.data;
  },
};

// =============================================================================
// Agent Endpoints
// =============================================================================

export const agentApi = {
  /**
   * List all HC agents
   */
  list: async (): Promise<ApiResponse<Agent[]>> => {
    const response = await apiClient.get<ApiResponse<unknown[]>>('/agents');
    const raw = response.data;
    // Map DB field names (agent_id, agent_name, agent_type) to frontend fields (id, name, type)
    if (raw.success && Array.isArray(raw.data)) {
      raw.data = raw.data.map((a: any) => {
        const startedAt = a.started_at ? new Date(a.started_at).getTime() : 0;
        const uptimeSeconds = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
        return {
          id: a.agent_id ?? a.id,
          name: a.agent_name ?? a.name,
          type: a.agent_type ?? a.type,
          status: a.status,
          uptime: a.uptime ?? uptimeSeconds,
          last_activity: a.last_heartbeat ?? a.last_activity ?? '',
          metrics: {
            observations_processed: a.tasks_completed,
            success_rate: a.success_rate,
            actions_taken: a.tasks_failed != null ? (Number(a.tasks_completed) + Number(a.tasks_failed)) : undefined,
          },
          config: a.config ?? a.capabilities ?? {},
        };
      });
    }
    return raw as ApiResponse<Agent[]>;
  },

  /**
   * Get single agent status
   */
  get: async (id: string): Promise<ApiResponse<Agent>> => {
    const response = await apiClient.get<ApiResponse<Agent>>(`/agents/${id}`);
    return response.data;
  },

  /**
   * Start an agent
   */
  start: async (id: string): Promise<ApiResponse<Agent>> => {
    const response = await apiClient.post<ApiResponse<Agent>>(`/agents/${id}/start`);
    return response.data;
  },

  /**
   * Stop an agent
   */
  stop: async (id: string): Promise<ApiResponse<Agent>> => {
    const response = await apiClient.post<ApiResponse<Agent>>(`/agents/${id}/stop`);
    return response.data;
  },

  /**
   * Restart an agent
   */
  restart: async (id: string): Promise<ApiResponse<Agent>> => {
    const response = await apiClient.post<ApiResponse<Agent>>(`/agents/${id}/restart`);
    return response.data;
  },
};

// =============================================================================
// Learner Export Endpoints
// =============================================================================

export const learnerApi = {
  /**
   * Export labeled data for HC-Learner training
   */
  exportData: async (): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>('/learner/export');
    return response.data;
  },
};

// Export default API object with all endpoints
// WP-HC6 (bug-1466): knowledgeApi/settingsApi removed along with the
// KnowledgeGraph.tsx/Settings.tsx mock pages that were their only callers
// — their backend endpoints never existed.
const hcApi = {
  health: healthApi,
  dashboard: dashboardApi,
  observations: observationApi,
  policies: policyApi,
  audits: auditApi,
  agents: agentApi,
  learner: learnerApi,
};

export default hcApi;
